<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="[
        $class(headerState),
        {
          'site-header--hasOverlay': activeMegaMenuId || getActiveOverlay,
          'inspector-mode': inspectorMode,
          'has-border': isTechSpecPage,
        },
      ]"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
      @click="marketSwitcherCloseOverlay"
    >
      <div class="site-header__masthead">
        <div class="site-header__container">
          <!-- Menu icon -->
          <button
            class="site-header__menu icon-button"
            type="button"
            :aria-label="$string('general.site_header.menu')"
            @click="
              toggleOverlay({
                component: 'menu-drawer',
                ignoreDismissed: true,
                namespace: 'menuDrawer',
              })
            "
          >
            <span
              class="site-header__hamburger hamburger"
              :class="{ [$class('active')]: getActiveOverlay === 'menuDrawer' }"
            >
              <span class="hamburger__inner hamburger__inner-1" />
              <span class="hamburger__inner hamburger__inner-2" />
              <span class="hamburger__inner hamburger__inner-3" />
            </span>
          </button>

          <div class="site-header__left">
            <nav class="site-header__navigation">
              <ul class="site-header__navigation-list">
                <li
                  v-for="primaryLink of primaryLinks"
                  :key="primaryLink.title"
                  class="site-header__navigation-item"
                  :class="{
                    'site-header__navigation-item--active': activeMegaMenuId === primaryLink.title,
                  }"
                >
                  <a
                    class="site-header__navigation-link text-button-m"
                    :href="primaryLink.url"
                    @mouseover="(evt) => toggleMegaMenu(
                      primaryLink.isMegaMenu ? primaryLink.title : null, evt, 'hover')"
                    v-text="primaryLink.title"
                  />
                </li>
              </ul>
            </nav>
          </div>

          <a
            class="site-header__logo"
            :href="$variable('routes.root')"
            :title="$string('accessibility.site_header.logo')"
            :aria-label="$string('accessibility.site_header.logo')"
          >
            <component
              :is="titleElement"
              id="site-header-title"
              class="site-header__logo-text"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.site_header.logo')"
              />
              <icon-grau />
            </component>
          </a>

          <div class="site-header__right">
            <ul class="site-header__list">
              <li
                v-if="liquid.settings.cta_url"
                class="site-header__list-item site-header__list-item-custom-text"
              >
                <a
                  class="site-header__custom-text text-button-m"
                  :href="liquid.settings.cta_url"
                  v-html="liquid.settings.cta_label"
                />
              </li>

              <li
                v-if="marketSwitcherEnabled"
                class="site-header__list-item site-header__list-item-market-switcher text-button-m"
              >
                <market-switcher-trigger placement="header" />
              </li>

              <li
                v-if="liquid.settings.store_locator_icon_enable"
                class="site-header__list-item site-header__list-item-store-locator"
              >
                <a
                  class="site-header__store-locator-link"
                  :href="liquid.settings.store_locator_icon_url"
                  :aria-label="$string('general.site_header.store_locator')"
                >
                  <icon-store-locator />
                </a>
              </li>

              <li
                v-if="liquid.settings.search_icon_enable"
                class="site-header__list-item site-header__list-item-search"
              >
                <button
                  class="site-header__search icon-button"
                  type="button"
                  :aria-label="$string('general.site_header.search')"
                  @click="
                    toggleOverlay({
                      component: 'predictive-search',
                      ignoreDismissed: true,
                      namespace: 'predictiveSearch',
                    })
                  "
                >
                  <icon-search />
                </button>
              </li>

              <li
                v-if="liquid.settings.account_icon_enable"
                class="site-header__list-item site-header__list-item-account"
              >
                <a
                  class="site-header__account icon-button"
                  :href="$variable('routes.account.url')"
                  :aria-label="$string('general.site_header.account')"
                >
                  <icon-account />
                </a>
              </li>

              <li
                v-if="liquid.settings.cart_icon_enable"
                class="site-header__list-item site-header__list-item-cart"
              >
                <button
                  class="site-header__cart icon-button"
                  type="button"
                  :aria-label="$string('general.site_header.cart')"
                  @click="handleCartToggle"
                >
                  <icon-cart />

                  <span
                    v-if="cart.item_count > 0"
                    class="site-header__cart-badge"
                    aria-hidden="true"
                  />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </header>

    <overlay
      key="megaMenu"
      block-class="mega-menu-overlay"
      direction="down"
      namespace="megaMenu"
      type="drawer"
      transition="none"
      :disable-scroll="false"
      close-on-mouse-leave
    >
      <template #body="{ isActive }">
        <mega-menu
          :is-active="isActive"
          :settings="settings"
          :active-id="activeMegaMenuId"
        />
      </template>
    </overlay>

    <!-- Menu drawer -->
    <overlay
      key="predictiveSearch"
      block-class="predictive-search-overlay"
      direction="down"
      namespace="predictiveSearch"
      type="drawer"
      transition="none"
      :disable-scroll="isMobile"
      close-on-mouse-leave
    >
      <template #body="{ isActive }">
        <predictive-search
          :is-active="isActive"
          :liquid="liquid"
          :quick-access="searchLinks"
          :default-products="searchDefaultProducts"
        />
      </template>
    </overlay>

    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menuDrawer"
      :template="false"
      type="drawer"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :mobile-footer="mobileFooter"
          :settings="settings"
          :is-active="isActive"
        />

        <div
          v-if="!getComponentActive('menu-drawer')"
          class="site-header__menu-drawer"
        >
          <div
            v-for="(_, index) of [...Array(mobile.itemCount)]"
            :key="`menu-drawer-loading-${index}`"
            class="site-header__menu-drawer-link"
          >
            <span
              class="site-header__menu-drawer-loading"
              :style="{
                animationDelay: $timing('quick', { index, unit: 's' }),
              }"
            />
          </div>
        </div>
      </template>
    </overlay>

    <overlay
      v-if="marketSwitcherEnabled"
      key="marketSwitcher"
      block-class="market-switcher-overlay"
      direction="up"
      namespace="marketSwitcher"
      type="modal"
      transition="none"
      :teleport="false"
    >
      <template #body>
        <market-switcher />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import MarketSwitcher from '~global/market-switcher/market-switcher'
import MarketSwitcherTrigger from '~global/market-switcher/market-switcher-trigger'
import Overlay from '~global/overlay/overlay'

import IconAccount from '~icons/misc/account.svg'
import IconCart from '~icons/misc/cart.svg'
import IconGrau from '~icons/misc/grau.svg'
import IconSearch from '~icons/misc/search.svg'
import IconStoreLocator from '~icons/misc/store-locator.svg'

export default {
  name: 'SiteHeader',

  components: {
    IconAccount,
    IconCart,
    IconGrau,
    IconSearch,
    IconStoreLocator,
    MarketSwitcher,
    MarketSwitcherTrigger,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Overlay,
    PredictiveSearch: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.predictive-search' */
        '~async/predictive-search/predictive-search'
      ),
    }),
    MegaMenu: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.mega-menu' */
        '~async/mega-menu/mega-menu'
      ),
    }),
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    mobileFooter: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    searchLinks: {
      type: Array,
      default: () => [],
    },
    searchDefaultProducts: {
      type: Array,
      default: () => [],
    },
    titleElement: {
      type: String,
      default: 'h1',
    },
  },

  data() {
    const primaryLinks = this.settings.blocks
      .filter((block) => block.type === 'primary-link')
      .map((primaryLink) => {
        const desktopBlockIds = primaryLink.desktopBlockIds.split(',').filter(Boolean)
        return {
          ...primaryLink,
          desktopBlockIds: primaryLink.desktopBlockIds.split(','),
          isMegaMenu: desktopBlockIds.length > 0,
        }
      })

    return {
      height: {
        total: 0,
        visible: 0,
      },
      inspectorMode: false,
      marketSwitcherEnabled: cnvs?.settings?.marketSwitcher,
      isTechSpecPage:
        window.cnvs.page.template.suffix === 'tech-spec' &&
        window.cnvs.page.type === 'product',
      primaryLinks,
      activeMegaMenuId: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
    }),
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible'() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },
  },

  mounted() {
    nextTick().then(() => {
      this.setHeights(true)
    })
    this.setEventBusListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      loadComponent: 'components/load',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('overlay:closed', () => {
        this.setActiveMegaMenuId(null)
      })
    },

    setActiveMegaMenuId(id) {
      this.activeMegaMenuId = id
    },

    toggleMegaMenu(id, evt, eventType) {
      if (id === null) {
        this.closeOverlay('megaMenu')
        this.setActiveMegaMenuId(null)
        return
      }

      evt.preventDefault()
      evt.stopImmediatePropagation()

      // Close the mega menu if the same option is clicked on
      if (eventType !== 'hover') {
        if (this.activeMegaMenuId === id || id === null) {
          this.closeOverlay('megaMenu')
          this.setActiveMegaMenuId(null)
          return
        }
      }

      // Initially open the mega menu
      if (this.getActiveOverlay !== 'megaMenu') {
        this.toggleOverlay({
          component: 'mega-menu',
          ignoreDismissed: true,
          namespace: 'megaMenu',
        })
      }

      this.setActiveMegaMenuId(id)
    },

    /**
     * Set heights of header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (this.inspectorMode) {
        return
      }

      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      this.height.visible =
        this.headerState === 'closed' ? 0 : this.$refs.siteHeader.offsetHeight
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle() {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Close market switcher overlay
     */
    marketSwitcherCloseOverlay() {
      if (this.getActiveOverlay === 'marketSwitcher') {
        this.closeOverlay('marketSwitcher')
      }
    },

    /**
     * Shopify inspector activate event.
     * - Disable fixed positioning.
     */
    shopifyInspectorActivate() {
      this.inspectorMode = true
    },

    /**
     * Shopify inspector deactivate event.
     */
    shopifyInspectorDeactivate() {
      this.inspectorMode = false
    },
  },
}
</script>
